/* Only gets fired on the client */
import { replace } from 'react-router-redux';
import Promise from 'bluebird';
import { AdobePass, Api, Device, ContentType, Flags, ModalType } from 'enums';
import { GEO_LOCATION_KEY, SUB_KEY, SESSION_TOKEN_KEY, USER_TRACKING_KEY, BAM_PROXY_IFRAME_ID } from 'constants';
import { replaceHero } from 'common/page/actions';
import { initAdsConfig } from 'common/ads/actions';
import { loadBootstrap } from 'common/actions/apiAction';
import { completeOneIDAuthentication } from 'common/actions/oneIdActions';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import appSelector, { contentIdInPlayerUrl } from 'common/selectors/appSelector';
import { persistentFlagsSelector } from 'common/selectors/flagsSelector';
import pageSelector from 'common/selectors/pageSelector';
import routingSelector from 'common/selectors/routingSelector';
import oneIdSelector, {
    oneIdTokenSelector,
    oneIdLoggedInSelector,
    oneIdEntitlementsSelector
} from 'common/selectors/oneIdSelector';
import {
    passOneIdTokenSelector,
    bamSessionTokenSelector,
    superBundleSelector
} from 'common/selectors/authenticationSelectors';
import selectedContentSelector from 'common/selectors/selectedContentSelector';
import selectedStreamSelector from 'common/selectors/selectedStreamSelector';
import {
    createAdobePassErrorAction,
    createStartCheckBAMAuthenticationAction,
    createCompleteCheckBAMAuthenticationAction,
    createGetBAMAuthenticationInfoAction,
    createGetGeoLocationAction,
    createGetBAMSessionTokenAction,
    createGetUserTrackingAction
} from 'common/actions/authenticationActions';
import { getContentPlaylistFromBuckets } from 'webapp/player/util';
import WatchESPNWebPlayer from 'webapp/common/video-player';
import { isEmpty, getBamAuthConfig } from 'lib/util';
import { get, set } from 'lib/util/localStorage';
import * as perf from 'lib/util/perf';
import { getPlaybackMeta } from 'lib/api/PageAPI';
import { hasTDA } from 'lib/auth';

export const APP_RENDERED = 'APP_RENDERED';
export const ANALYTICS_READY = 'ANALYTICS_READY';
export const CLOSE_MODAL_MESSAGE = 'CLOSE_MODAL_MESSAGE';
export const CONTENT_SELECT = 'CONTENT_SELECT';
export const CONTENT_UNSELECT = 'CONTENT_UNSELECT';
export const STREAM_SELECTING = 'STREAM_SELECTING';
export const STREAM_SELECT = 'STREAM_SELECT';
export const STREAM_UNSELECT = 'STREAM_UNSELECT';
export const DISPLAY_MODAL_MESSAGE = 'DISPLAY_MODAL_MESSAGE';
export const MEDIA_QUERY = 'MEDIA_QUERY';
export const INIT_APP_DATA = 'INIT_APP_DATA';
export const FETCH_EXTERNAL_CONFIG = 'FETCH_EXTERNAL_CONFIG';
export const FETCH_EMBEDDED_OPTIONS = 'FETCH_EMBEDDED_OPTIONS';
export const PACKAGE_ID = 'PACKAGE_ID';
export const PACKAGE_TIER = 'PACKAGE_TIER';
export const PICKER_STREAMS_SELECT = 'PICKER_STREAMS_SELECT';
export const PICKER_STREAMS_UNSELECT = 'PICKER_STREAMS_UNSELECT';
export const PICKER_STREAMS_SELECT_ERROR = 'PICKER_STREAMS_SELECT_ERROR';
export const OPEN_PICKER_SUCCESS = 'OPEN_PICKER_SUCCESS';
export const OPEN_PICKER_ERROR = 'OPEN_PICKER_ERROR';
export const PICKER_AUTO_SELECT = 'PICKER_AUTO_SELECT';
export const PICKER_STATE_CLEAR = 'PICKER_STATE_CLEAR';


const { Env: AdobePassEnv } = AdobePass;
const { Env: ApiEnv } = Api;

export function displayModalMessage(messageType, messageData) {
    return {
        type: DISPLAY_MODAL_MESSAGE,
        messageType,
        messageData
    };
}

export function closeModalMessage() {
    return {
        type: CLOSE_MODAL_MESSAGE
    };
}

export function updateRoute(query) {
    return (dispatch, getState) => {
        const state = getState();
        const flags = persistentFlagsSelector(state);
        const { locationBeforeTransitions } = routingSelector(state);
        dispatch(replace({ ...locationBeforeTransitions, query: { ...flags, ...query } }));
    };
}

export function streamSelect(stream) {
    return async (dispatch, getState) => {
        if (isEmpty(stream)) {
            console.error('Empty stream selected');
            return;
        }
        const state = getState();
        const { selectingStream, playbackMeta: playbackMetaArg = {} } = appSelector(state);
        const { id: pageId, buckets = [], tracking } = pageSelector(state);
        const { locationBeforeTransitions } = routingSelector(state);
        const { query = {} } = locationBeforeTransitions || {};
        const content = selectedContentSelector(state);
        const selectedStream = selectedStreamSelector(state);
        const { type, bucketId } = content;
        const reselected = (
            (selectingStream && selectingStream.id === stream.id) ||
            (playbackMetaArg.playbackState && playbackMetaArg.playbackState.id === stream.id)
        );

        if (reselected) {
            return;
        }

        // NOTE: Assigning the playlist array to the stream without mobile restrictions
        if (type === ContentType.VOD) {
            stream.playlist = getContentPlaylistFromBuckets(content, buckets);
        }

        dispatch({
            type: STREAM_SELECTING,
            stream
        });

        let playbackMeta;
        if (stream.links && stream.links.play) {
            try {
                playbackMeta = await getPlaybackMeta(stream.links.play);
                const { selectingStream: selectingStreamArg } = appSelector(getState());
                if (!selectingStreamArg) {
                    return;
                }
            } catch (err) {
                console.error(err);
            }
        }

        const { id } = stream;
        const { tracking: { sport, league, franchise } = {} } = playbackMeta || {};
        const playerUrlId = contentIdInPlayerUrl(state);
        if (playerUrlId && selectedStream.id !== id) {
            if ((query.id && !query.eventId && !query.gameId) || !stream.content.eventId) {
                dispatch(updateRoute({ id, bucketId }));
            }
        }

        // reset AdobePass error
        dispatch(createAdobePassErrorAction());

        dispatch({
            type: STREAM_SELECT,
            stream,
            playbackMeta,
            clickedTime: Date.now()
        });

        // refresh ads with the new ad targeting
        if (pageId === 'player') {
            // TODO: i think we can pass the value as is by encoding the value, but that might involves espn.com change.
            const regex = (/\s|:|#|,|\(|\)|\[|]/g);
            const sp = sport && sport.replace(regex, '_');
            const lg = league && league.replace(regex, '_');
            const show = franchise && franchise.replace(regex, '_');
            dispatch(initAdsConfig(pageId, tracking, {
                sp,
                lg,
                show
            }, query));
        }
    };
}

export function streamUnSelect() {
    return {
        type: STREAM_UNSELECT
    };
}

export function contentSelect(contentArg) {
    return async (dispatch, getState) => {
        const state = getState();
        const content = contentArg;
        let stream;
        const { id } = pageSelector(state);
        if (id === 'player') {
            if (content) {
                dispatch(replaceHero(content));
            }
        }

        dispatch({
            type: CONTENT_SELECT,
            content,
            clickedTime: Date.now()
        });

        if (stream) {
            dispatch(streamSelect(stream));
        }
    };
}

export function contentUnSelect(content) {
    return {
        type: CONTENT_UNSELECT,
        content,
        clickedTime: Date.now()
    };
}

export function appRendered() {
    return {
        type: APP_RENDERED
    };
}

export function analyticsReady(initData = {}) {
    return {
        type: ANALYTICS_READY,
        initData,
        completeTime: Date.now()
    };
}

export function fetchExternalConfig() {
    return async (dispatch) => {
        const config = await dispatch(loadBootstrap());
        dispatch({
            type: FETCH_EXTERNAL_CONFIG,
            bootstrap: config
        });
        return config;
    };
}
// TODO: DTC - point back to `ApiEnv.PROD`
export function initAppData({
    appName,
    adobePassEnv = AdobePassEnv.PROD,
    apiEnv = ApiEnv.PROD,
    configEnv,
    device,
    env,
    location,
    lang,
    country,
    messages,
    enableSearch,
    packageId,
    tier,
    edition,
    webview,
    renderTemplateType,
    newrelicInsertKey,
    newrelicAccountId
}) {
    const isMobile = (device === Device.HANDSET || device === Device.TABLET);

    return {
        type: INIT_APP_DATA,
        appName,
        adobePassEnv,
        apiEnv,
        configEnv,
        device,
        env,
        isMobile,
        location,
        lang,
        sourceLang: lang,
        country,
        messages,
        enableSearch,
        packageId,
        tier,
        edition,
        webview,
        renderTemplateType,
        newrelicInsertKey,
        newrelicAccountId
    };
}

export function mediaQuery(mq) {
    return {
        type: MEDIA_QUERY,
        mq
    };
}

export function savePackageId(packageId) {
    return {
        type: PACKAGE_ID,
        packageId
    };
}

export function savePackageTier(tier) {
    return {
        type: PACKAGE_TIER,
        tier
    };
}

export function sendContentReward({
    selector = BAM_PROXY_IFRAME_ID,
    rewardToken,
    contentId,
    userActivityAction = 'CLICK',
    recommendedContentIds = []
}) {
    return async (dispatch, getState) => {
        const state = getState();
        const { bam } = bootstrapSelector(state);
        const { locationBeforeTransitions } = routingSelector(state);
        const { query } = locationBeforeTransitions || {};
        try {
            const videoPlayer = new WatchESPNWebPlayer({
                bamPlayerOptions: query[Flags.BAM_ENV] ? { ...bam, env: query[Flags.BAM_ENV] } : bam
            });

            await videoPlayer.sendContentReward({
                selector: `#${selector}`,
                rewardToken,
                contentId,
                userActivityAction,
                recommendedContentIds
            });
        } catch (error) {
            console.error('sendContentReward ERROR:', error); // eslint-disable-line
        }
    };
}

export function createSDKWrapper(containerIdArg, requestsArg, transactionId = 0) {
    return (dispatch, getState) => {
        const perfName = requestsArg ? `createSDKWrapper_${requestsArg.join('')}` : 'createSDKWrapper';
        perf.markStart(perfName);
        const state = getState();
        const { bam } = bootstrapSelector(state);
        const passOneIdToken = passOneIdTokenSelector(state);
        const { locationBeforeTransitions } = routingSelector(state);
        const { query } = locationBeforeTransitions || {};
        const isOneIdLoggedIn = oneIdLoggedInSelector(state);
        const oneIdToken = oneIdTokenSelector(state);
        dispatch(createStartCheckBAMAuthenticationAction());
        return new Promise(async (resolve) => {
            const geolocationKey = 'geolocation';
            const subscriptionsKey = 'subscriptions';
            const sessionTokenKey = 'sessionToken';
            const userTrackingKey = 'userTrackingInformation';
            const result = {};
            const requests = requestsArg || [geolocationKey, subscriptionsKey, sessionTokenKey, userTrackingKey];
            const videoPlayer = new WatchESPNWebPlayer({
                bamPlayerOptions: query[Flags.BAM_ENV] ? { ...bam, env: query[Flags.BAM_ENV] } : bam
            });
            const geo = get(GEO_LOCATION_KEY);

            const complete = () => {
                dispatch(createCompleteCheckBAMAuthenticationAction());
                perf.measureAndTime(perfName);
                resolve(result);
            };
            const defaultActions = () => {
                if (!geo) {
                    dispatch(createGetGeoLocationAction());
                }
                dispatch(createGetBAMAuthenticationInfoAction());
                complete();
            };
            const removeRequest = (list, entry) => {
                const index = list.indexOf(entry);
                if (index > -1) {
                    list.splice(index, 1);
                }
            };

            if (requests.indexOf(geolocationKey) > -1) {
                if (geo) { // use cached one
                    const location = geo;
                    dispatch(createGetGeoLocationAction(location));
                    result[geolocationKey] = location;
                    removeRequest(requests, geolocationKey);
                } else {
                    videoPlayer.once(WatchESPNWebPlayer.Events.GEOLOCATION_READY, ({ body: { location } = {} } = {}) => {
                        dispatch(createGetGeoLocationAction(location));
                        result[geolocationKey] = location;
                        if (Object.keys(result).length >= requests.length) {
                            complete();
                        }
                    });
                }
            }
            if (requests.indexOf(subscriptionsKey) > -1) {
                if (!isOneIdLoggedIn) {
                    dispatch(createGetBAMAuthenticationInfoAction());
                    removeRequest(requests, subscriptionsKey);
                } else {
                    videoPlayer.once(WatchESPNWebPlayer.Events.SUBSCRIPTIONS_READY, ({ body: { subscriptions } = {} } = {}) => {
                        dispatch(createGetBAMAuthenticationInfoAction(subscriptions));
                        const newState = getState();
                        const entitlements = oneIdEntitlementsSelector(newState);
                        const bamSessionToken = bamSessionTokenSelector(newState);
                        const hasSuperBundle = superBundleSelector(newState);
                        const hasTDAEntitlement = hasTDA(bamSessionToken);
                        const isAuthenticated = (entitlements && entitlements.length > 0) || hasTDAEntitlement;
                        const { swid } = oneIdSelector(getState());
                        set(SUB_KEY, { swid, subscriptions }, 24 * 60 * 60);
                        dispatch(completeOneIDAuthentication(
                            isAuthenticated, entitlements, hasTDAEntitlement, hasSuperBundle, true
                        ));
                        result[subscriptionsKey] = subscriptions;
                        if (Object.keys(result).length >= requests.length) {
                            complete();
                        }
                    });
                }
            }

            if (requests.indexOf(userTrackingKey) > -1) {
                videoPlayer.once(WatchESPNWebPlayer.Events.USER_TRACKING_INFORMATION_READY, ({ body = {} }) => {
                    set(USER_TRACKING_KEY, { ...body }, 24 * 60 * 60);
                    dispatch(createGetUserTrackingAction(body));
                    result[userTrackingKey] = body;
                    if (Object.keys(result).length >= requests.length) {
                        complete();
                    }
                });
            }


            if (requests.indexOf(sessionTokenKey) > -1) {
                if (!isOneIdLoggedIn) {
                    dispatch(createGetBAMSessionTokenAction());
                    removeRequest(requests, sessionTokenKey);
                } else {
                    videoPlayer.once(WatchESPNWebPlayer.Events.SESSIONTOKEN_READY, ({ body: { token } = {} } = {}) => {
                        dispatch(createGetBAMSessionTokenAction(token));
                        // NOTE: Setting the sessionToken in temporary storage for subsequent calls to have quick access
                        // On every page transition/load a new sessionToken will always be requested and re-assigned.
                        set(SESSION_TOKEN_KEY, token, 3600);
                        result[sessionTokenKey] = token;
                        if (Object.keys(result).length >= requests.length) {
                            complete();
                        }
                    });
                }
            }

            if (!requests.length) {
                defaultActions();
            } else {
                try {
                    const appContainerEl = document.getElementById('app');
                    let containerId = containerIdArg;
                    let el = document.getElementById(containerId);
                    let elCnt = 0;
                    if (el) {
                        while (el) {
                            elCnt += 1;
                            containerId = `${containerIdArg}${elCnt}`;
                            el = document.getElementById(containerId);
                        }
                    }
                    el = document.createElement('div');
                    el.id = containerId;
                    el.className = containerIdArg;
                    appContainerEl.appendChild(el);
                    let eventRes = [userTrackingKey];
                    if (isOneIdLoggedIn) {
                        eventRes = [geolocationKey, subscriptionsKey, sessionTokenKey, userTrackingKey];
                    }
                    const config = {
                        container: `#${containerId}`,
                        customProps: {},
                        ...getBamAuthConfig(oneIdToken, passOneIdToken),
                        requests: eventRes,
                        transactionId,
                        timeout: !(requests.length === 1 && requests.indexOf(subscriptionsKey) > -1)
                    };
                    console.log('createSDKWrapperConfig', config); // eslint-disable-line no-console
                    await videoPlayer.createSDKWrapper(config);
                } catch (err) {
                    console.error(err);
                    defaultActions();
                }
            }
        });
    };
}

/**
 * @description  Action to trigger watch web stream picker
 * It is not being used in currently. It has been replaced with .com stream picker.
 * We should remove it after .com stream picker is stable.
 * @param {object} content
 */
export function createSelectMultipleStreamsAction(content = {}) {
    return (dispatch) => {
        if (content.streams.length) {
            dispatch(displayModalMessage(ModalType.BROADCAST_PICKER, content));
        }
    };
}

export function pickerStreamsSelect(pickerStreams, pickerStreamsEventId) {
    return {
        type: PICKER_STREAMS_SELECT,
        pickerStreams,
        pickerStreamsEventId
    };
}

export function pickerStreamsUnSelect() {
    return {
        type: PICKER_STREAMS_UNSELECT
    };
}

export function pickerStreamsSelectError(error) {
    return {
        type: PICKER_STREAMS_SELECT_ERROR,
        error
    };
}

export function openPickerSuccess(payload) {
    return {
        type: OPEN_PICKER_SUCCESS,
        payload
    };
}

export function openPickerError(error) {
    return {
        type: OPEN_PICKER_ERROR,
        error
    };
}

export function pickerAutoSelect(stream) {
    return {
        type: PICKER_AUTO_SELECT,
        stream
    };
}
export function pickerStateClear() {
    return {
        type: PICKER_STATE_CLEAR
    };
}



// WEBPACK FOOTER //
// ./app/common/actions/appActions.js